import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { CreateInstituicaoEnsinoDto, ReadInstituicaoEnsinoDto, UpdateInstituicaoEnsinoDto } from '@/api.types';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { FieldError, formControlClass } from '@/shared/components/forms/FormFieldFeedback';
import { FormataTelefone } from '@/shared/utils/telefones';
import { numberInputValueAs } from '@/shared/utils/formZod.utils';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { instituicaoFormSchema } from '@/modules/structure/instituicoes/instituicao.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { SubmitHandler, useForm, useFormState, type FieldErrors, type Resolver, } from 'react-hook-form';
import { buildInstituicaoSavePayload, normalizeInstituicaoFormFromApi, } from '@/modules/structure/instituicoes/instituicao-form.utils';
import { Tabelas } from '@/shared/constants/tables';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { validaCNPJ } from '@/shared/utils/cnpj';

export default function InstituicoesEditPage() {
  const { register, handleSubmit, reset, getValues, setValue, control } =
    useForm<ReadInstituicaoEnsinoDto>({
      resolver: zodResolver(instituicaoFormSchema as never) as Resolver<ReadInstituicaoEnsinoDto>,
      mode: 'onTouched',
      reValidateMode: 'onChange',
      shouldFocusError: false,
      defaultValues: {
        ordem: 0,
        telefone: { ddd: 79, numero: '' },
      },
    });
  const { errors } = useFormState({ control });
  const { setActions } = useBreadcrumbActions();
  const instituicaoService = new InstituicaoEnsinoService();

  const navigate = useNavigate();
  const params = useParams();
  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', href: Links.Instituicoes_Criar, onClick: () => { } },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions]);

  useEffect(() => {
    if (id > 0)
      instituicaoService
        .Read(id)
        .then((result) => {
          if (result) {
            const normalized = normalizeInstituicaoFormFromApi(result);
            normalized.cnpj = validaCNPJ(normalized.cnpj, true);
            if (normalized.telefone?.numero) {
              normalized.telefone.numero = FormataTelefone(normalized.telefone.numero);
            }
            reset(normalized);
          }
        })
        .catch((res) => {
          toast.error((res as string) + '');
        });
  }, [id]);

  const onSubmit: SubmitHandler<ReadInstituicaoEnsinoDto> = (data) => {
    setIsLoading(true);
    const payload = buildInstituicaoSavePayload(data, id > 0 ? id : undefined);

    const request = id > 0
      ? instituicaoService.Update(id, payload as UpdateInstituicaoEnsinoDto)
      : instituicaoService.Create(payload as CreateInstituicaoEnsinoDto);

    request
      .then(() => {
        toast.success(
          id > 0 ? 'Instituição atualizada com sucesso.' : 'Instituição criada com sucesso.',
        );
        navigate(`${Links.Instituicoes_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setModalExcluirShow(false);
    instituicaoService
      .Delete(id)
      .then(() => {
        toast.success('Instituição removida.');
        navigate(`${Links.Instituicoes_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  };

  const onInvalid = (fieldErrors: FieldErrors<ReadInstituicaoEnsinoDto>) => {
    if (import.meta.env.DEV) {
      console.warn('[InstituicaoEdit] Validação:', fieldErrors);
    }
    toast.error('Corrija os campos destacados antes de salvar.');
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Instituição de Ensino</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit, onInvalid)(event);
        }}
      >
        <div className="row">
          <div className="mb-3 col-md-1">
            <label htmlFor="ordem" className="form-label">
              Ordem: <span className="text-danger">*</span>
            </label>
            <input
              id="ordem"
              type="number"
              title="Ordem no relatório"
              className={formControlClass(!!errors.ordem)}
              min={0}
              {...register('ordem', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.ordem?.message} />
          </div>

          <div className="mb-3 col-md-3">
            <label htmlFor="cnpj" className="form-label">
              CNPJ: <span className="text-danger">*</span>
            </label>
            <input
              id="cnpj"
              autoFocus={true}
              title="CNPJ"
              type="text"
              className={formControlClass(!!errors.cnpj)}
              maxLength={18}
              {...register('cnpj', {
                onChange: (e) => {
                  const value = e.target.value;
                  setValue('cnpj', validaCNPJ(value, true));
                },
              })}
            />
            <FieldError message={errors.cnpj?.message} />
          </div>

          <div className="mb-3 col-md-8">
            <label htmlFor="razao_social" className="form-label">
              Razão Social: <span className="text-danger">*</span>
            </label>
            <input
              id="razao_social"
              title="Razão Social"
              type="text"
              className={formControlClass(!!errors.razao_social)}
              {...register('razao_social')}
            />
            <FieldError message={errors.razao_social?.message} />
          </div>

          <div className="mb-3 col-md-4">
            <label htmlFor="representante_cargo" className="form-label">
              Cargo do representante:
            </label>
            <input
              id="representante_cargo"
              title="Cargo do representante"
              type="text"
              className={formControlClass(!!errors.representante_cargo)}
              {...register('representante_cargo')}
            />
            <FieldError message={errors.representante_cargo?.message} />
          </div>

          <div className="mb-3 col-md-4">
            <label htmlFor="representante_nome" className="form-label">
              Representante:
            </label>
            <input
              id="representante_nome"
              title="Representante"
              type="text"
              className={formControlClass(!!errors.representante_nome)}
              {...register('representante_nome')}
            />
            <FieldError message={errors.representante_nome?.message} />
          </div>

          <div className="col-md-8 p-3">
            <div className="card">
              <div className="card-header">
                <span>Endereço</span>
              </div>
              <div className="card-body row">
                <div className=" col-md-4">
                  <label htmlFor="cep" className="form-label">
                    CEP: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="cep"
                    title="CEP"
                    type="text"
                    className={formControlClass(!!errors.endereco?.cep)}
                    maxLength={8}
                    {...register('endereco.cep')}
                  />
                  <FieldError message={errors.endereco?.cep?.message} />
                </div>
                <div className="col-md-4">
                  <label htmlFor="cidade" className="form-label">
                    Cidade: <span className="text-danger">*</span>
                  </label>
                  <input
                    title="Cidade"
                    type="text"
                    className={formControlClass(!!errors.endereco?.cidade)}
                    {...register('endereco.cidade')}
                  />
                  <FieldError message={errors.endereco?.cidade?.message} />
                </div>
                <div className="col-md-4">
                  <label htmlFor="bairro" className="form-label">
                    Bairro:
                  </label>
                  <input
                    id="bairro"
                    title="Bairro"
                    type="text"
                    className={formControlClass(!!errors.endereco?.bairro)}
                    {...register('endereco.bairro')}
                  />
                  <FieldError message={errors.endereco?.bairro?.message} />
                </div>
                <div className="col-md-5">
                  <label htmlFor="logradouro" className="form-label">
                    Logradouro: <span className="text-danger">*</span>
                  </label>
                  <input
                    title="Logradouro"
                    type="text"
                    className={formControlClass(!!errors.endereco?.logradouro)}
                    {...register('endereco.logradouro')}
                  />
                  <FieldError message={errors.endereco?.logradouro?.message} />
                </div>
                <div className="col-md-5">
                  <label htmlFor="complemento" className="form-label">
                    Complemento:
                  </label>
                  <input
                    id="complemento"
                    title="Complemento"
                    type="text"
                    className={formControlClass(!!errors.endereco?.complemento)}
                    {...register('endereco.complemento')}
                  />
                  <FieldError message={errors.endereco?.complemento?.message} />
                </div>
                <div className="col-md-2">
                  <label htmlFor="numero" className="form-label">
                    Número:
                  </label>
                  <input
                    id="numero"
                    title="Número"
                    type="text"
                    className={formControlClass(!!errors.endereco?.numero)}
                    {...register('endereco.numero')}
                  />
                  <FieldError message={errors.endereco?.numero?.message} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4 p-3">
            <div className="card">
              <div className="card-header">
                <span>Telefone</span>
              </div>
              <div className="card-body row">
                <div className="col-md-4">
                  <label htmlFor="telefone_ddd" className="form-label">
                    DDD: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="telefone_ddd"
                    type="number"
                    className={formControlClass(!!errors.telefone?.ddd)}
                    maxLength={3}
                    min={0}
                    max={99}
                    {...register('telefone.ddd', { setValueAs: numberInputValueAs })}
                  />
                  <FieldError message={errors.telefone?.ddd?.message} />
                </div>
                <div className="col-md-8 mb-3">
                  <label htmlFor="telefone_numero" className="form-label">
                    Telefone: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="telefone_numero"
                    type="text"
                    className={formControlClass(!!errors.telefone?.numero)}
                    maxLength={10}
                    {...register('telefone.numero', {
                      onChange: (e) => {
                        const value = e.target.value;
                        setValue('telefone.numero', FormataTelefone(value));
                      },
                    })}
                  />
                  <FieldError message={errors.telefone?.numero?.message} />
                </div>
              </div>
            </div>
          </div>

          <EditadoPorRodape
            dados={{
              editadoPor: {
                username: getValues()?.editadoPor?.username
              },
              editadoEm: getValues()?.editadoEm,
            }}
          />
          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      ></ModalExcluir>
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.instituicao_ensino}
        idRegistro={id}
      ></ModalHistorico>
    </>
  );
}
